import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Property, PropertySet } from '@vsolv/packages/properties/domain';
import { Observable, firstValueFrom } from 'rxjs';

interface Paginated<T> {
  items: T[];
}

interface RatingElement {
  id: string;
  title: string;
  parentId: string | null;
}

@Injectable()
export class PropertyService {
  constructor(private http: HttpClient) {}

  // Properties
  async retrieveProperty(propertyId: string) {
    return await firstValueFrom(this.http.get<Property.Model<Property.PropertyType>>(`/api/properties/${propertyId}`));
  }

  async listProperties(query: Property.ListPropertiesQueryRequest) {
    return await firstValueFrom(
      this.http.get<Property.ListPropertiesQueryResponse>(`/api/properties`, {
        params: {
          page: query.page || 1,
          limit: query.limit || 10,
          ...(query.type && { type: query.type }),
          ...(query.search && { search: query.search }),
        },
      })
    );
  }

  listProperties$(query: Property.ListPropertiesQueryRequest): Observable<Property.ListPropertiesQueryResponse> {
    return this.http.get<Property.ListPropertiesQueryResponse>(`/api/properties`, {
      params: {
        page: query.page || 1,
        limit: query.limit || 10,
        ...(query.type && { type: query.type }),
        ...(query.search && { search: query.search }),
      },
    });
  }

  async createBoolean(dto: Property.CreateBooleanPropertyDto) {
    return await firstValueFrom(this.http.post<Property.CreatePropertyResponse>(`/api/properties/boolean`, dto));
  }

  async createDate(dto: Property.CreateDatePropertyDto) {
    return await firstValueFrom(this.http.post<Property.CreatePropertyResponse>(`/api/properties/date`, dto));
  }

  async createNumber(dto: Property.CreateNumberPropertyDto) {
    return await firstValueFrom(this.http.post<Property.CreatePropertyResponse>(`/api/properties/number`, dto));
  }

  async createObject(dto: Property.CreateObjectPropertyDto) {
    return await firstValueFrom(this.http.post<Property.CreatePropertyResponse>(`/api/properties/object`, dto));
  }

  async updateObject(propertyId: string, dto: Property.UpdateObjectPropertyDto) {
    return await firstValueFrom(
      this.http.patch<Property.UpdateObjectPropertyCommandResponse>(`/api/properties/object/${propertyId}`, dto)
    );
  }

  async createText(dto: Property.CreateTextPropertyDto) {
    return await firstValueFrom(this.http.post<Property.CreatePropertyResponse>(`/api/properties/text`, dto));
  }

  async updateProperty(propertyId: string, dto: Property.UpdatePropertyDto) {
    return await firstValueFrom(this.http.patch<{ id: string; name: string }>(`/api/properties/${propertyId}`, dto));
  }

  async deleteProperty(propertyId: string) {
    return await firstValueFrom(this.http.delete<{ id: string }>(`/api/properties/${propertyId}`));
  }

  //Property Sets
  async retrievePropertySet(setId: string) {
    return await firstValueFrom(this.http.get<PropertySet.Model>(`/api/property-sets/${setId}`));
  }

  async listDimensionElements(dimensionId: string) {
    const response = await firstValueFrom(
      this.http.get<Paginated<RatingElement>>('/api/rating/elements', {
        params: {
          dimensionId,
          page: 1,
          limit: 200,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
      })
    );

    return response.items.map(element => ({
      id: element.id,
      title: element.title,
      parentId: element.parentId,
    }));
  }

  async listPropertySets(query: PropertySet.ListPropertySetsQueryRequest) {
    return await firstValueFrom(
      this.http.get<PropertySet.ListPropertySetsQueryResponse>(`/api/property-sets`, {
        params: {
          page: query.page || 1,
          limit: query.limit || 10,
          ...(query.search && { search: query.search }),
        },
      })
    );
  }

  listPropertySets$(
    query: PropertySet.ListPropertySetsQueryRequest
  ): Observable<PropertySet.ListPropertySetsQueryResponse> {
    return this.http.get<PropertySet.ListPropertySetsQueryResponse>(`/api/property-sets`, {
      params: {
        page: query.page || 1,
        limit: query.limit || 10,
        ...(query.search && { search: query.search }),
      },
    });
  }

  async createPropertySet(dto: PropertySet.CreatePropertySetRequest) {
    return await firstValueFrom(this.http.post<PropertySet.CreatePropertySetResponse>(`/api/property-sets`, dto));
  }

  async updatePropertySet(id: string, dto: { name: string; icon: string }) {
    return await firstValueFrom(this.http.put<{ id: string }>(`/api/property-sets/${id}`, dto));
  }

  async deletePropertySet(id: string) {
    return await firstValueFrom(this.http.delete<{ id: string }>(`/api/property-sets/${id}`));
  }

  // Property Set Assignments
  async assignPropertyToSet(setId: string, propertyId: string, body: { required?: boolean; order?: number } = {}) {
    return await firstValueFrom(this.http.post(`/api/property-sets/${setId}/properties/${propertyId}`, body));
  }

  async removePropertyFromSet(setId: string, propertyId: string) {
    return await firstValueFrom(this.http.delete(`/api/property-sets/${setId}/properties/${propertyId}`));
  }

  /**
   * Save a new display order for property and dimension assignments on a set.
   * The whole batch goes out in one request, and the backend applies it inside
   * a transaction so the rows can't end up half-updated.
   */
  async updateAssignmentOrder(
    setId: string,
    assignments: { id: string; kind: 'PROPERTY' | 'DIMENSION'; order: number }[]
  ) {
    return await firstValueFrom(
      this.http.patch<{ success: boolean; updated: number }>(`/api/property-sets/${setId}/assignments/order`, {
        assignments,
      })
    );
  }
}
